import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { StatusPill } from "@/components/StatusPill";
import { PhaseStepper } from "@/components/PhaseStepper";
import { PHASES, fmtDate } from "@/lib/constants";
import { Download, FileUp } from "lucide-react";
import { toast } from "sonner";
import { Discovery2Section } from "./Discovery2Section";

export default function ProjectDossier() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [discovery, setDiscovery] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [audits, setAudits] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [findings, setFindings] = useState([]);
  const [trail, setTrail] = useState([]);

  const load = async () => {
    const [p, d, pr, b, fn, rv, au, ap, dl, tr] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/discovery`).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/prompts`).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/builds`).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/findings`).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/reviews`).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/audits`).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/approvals`).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/deliverables`).catch(() => ({ data: [] })),
      api.get(`/projects/${projectId}/trail`).catch(() => ({ data: [] })),
    ]);
    setProject(p.data);
    setDiscovery(d.data);
    setPrompts(pr.data);
    setBuilds(b.data);
    setReviews(rv.data);
    setAudits(au.data);
    setApprovals(ap.data);
    setDeliverables(dl.data);
    setFindings(fn.data);
    setTrail(tr.data);
  };
  useEffect(() => { load(); }, [projectId]);

  const setPhase = async (phase) => {
    try {
      await api.patch(`/projects/${projectId}`, { current_phase: phase });
      toast.success(`Phase aktualisiert: ${phase}`);
      load();
    } catch (e) {
      toast.error("Phase konnte nicht aktualisiert werden");
    }
  };

  const downloadYaml = async () => {
    const r = await api.get(`/projects/${projectId}/yaml`, { responseType: "blob" });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${projectId}.yaml`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const importYaml = async () => {
    const text = window.prompt("project.yaml einfügen:");
    if (!text) return;
    try {
      await api.post(`/projects/${projectId}/yaml`, { yaml: text });
      toast.success("project.yaml gespeichert");
      load();
    } catch (e) {
      toast.error("YAML-Import fehlgeschlagen");
    }
  };

  if (!project) return <InternalLayout><div className="font-mono text-xs text-neutral-500">LADE …</div></InternalLayout>;

  return (
    <InternalLayout>
      <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
        Projektakte · <span className="text-neutral-700">{project.project_id}</span>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-neutral-900" data-testid="dossier-title">{project.name}</h1>
          <div className="text-sm text-neutral-700 mt-1">{project.customer_org}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={downloadYaml} data-testid="export-yaml-button" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-neutral-300 hover:bg-neutral-50 rounded-sm">
            <Download className="w-3.5 h-3.5" /> project.yaml exportieren
          </button>
          <button onClick={importYaml} data-testid="import-yaml-button" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-neutral-300 hover:bg-neutral-50 rounded-sm">
            <FileUp className="w-3.5 h-3.5" /> YAML importieren
          </button>
        </div>
      </div>

      {/* Sign-off Banner: zeigt das letzte signierte Acceptance-Audit (read-only) */}
      {(() => {
        const signed = (audits || [])
          .filter((a) => a.type === "acceptance" && a.sign_off && a.sign_off.signed_off_at)
          .sort((a, b) => (a.sign_off.signed_off_at < b.sign_off.signed_off_at ? 1 : -1))[0];
        if (!signed) return null;
        return (
          <div
            data-testid="dossier-signoff-banner"
            className="mb-6 border border-emerald-300 bg-emerald-50 px-5 py-3 flex items-start gap-3"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700 mt-0.5">Sign-off</div>
            <div className="flex-1 text-sm text-emerald-900">
              <div className="font-medium">{signed.name}</div>
              <div className="text-xs">
                Signiert am {fmtDate(signed.sign_off.signed_off_at)} durch {signed.sign_off.signed_off_by_name}
              </div>
              <div className="text-xs italic mt-1">„{signed.sign_off.sign_off_statement}"</div>
            </div>
            <Link to={`/audits?project=${projectId}`} className="text-xs text-emerald-800 hover:underline">Audit öffnen →</Link>
          </div>
        );
      })()}

      {/* Phase stepper */}
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Phase setzen</div>
        <PhaseStepper current={project.current_phase} onChange={setPhase} />
      </div>

      {/* Header info grid */}
      <div className="akte-grid mb-8">
        <div className="akte-cell col-span-12 md:col-span-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Phase intern</div>
          <div className="mt-2"><StatusPill kind="phase" value={project.current_phase} /></div>
        </div>
        <div className="akte-cell col-span-12 md:col-span-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Phase Kunde</div>
          <div className="mt-2"><StatusPill kind="customer_phase" value={project.customer_phase} /></div>
        </div>
        <div className="akte-cell col-span-12 md:col-span-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Owner</div>
          <div className="mt-2 text-sm">{(project.members || []).find((m) => m.user_id === project.owner_id)?.name || "—"}</div>
        </div>
        <div className="akte-cell col-span-12 md:col-span-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Aktualisiert</div>
          <div className="mt-2 font-mono text-xs">{fmtDate(project.updated_at)}</div>
        </div>
      </div>

      {/* Counts */}
      <div className="akte-grid mb-8">
        {Object.entries(project.counts || {}).map(([k, v]) => (
          <div key={k} className="akte-cell col-span-6 md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{k.replace(/_/g, " ")}</div>
            <div className="font-display text-2xl mt-1 tabular text-neutral-900">{v}</div>
          </div>
        ))}
      </div>

      {/* Sections (links to deeper modules with project filter via state) */}
      <div className="grid md:grid-cols-2 gap-6">
        <Section title="Discovery Packs" testid="dossier-discovery" items={discovery.map((d) => ({
          id: d.pack_id, label: `Version ${d.version}`, status: d.status, sub: fmtDate(d.created_at),
        }))} link="/discovery" projectId={projectId} />
        <Section title="Prompt Sets" testid="dossier-prompts" items={prompts.map((p) => ({
          id: p.set_id, label: p.name, status: `v${p.current_version}`, sub: fmtDate(p.created_at), kindless: true,
        }))} link="/prompts" projectId={projectId} />
        <Section title="Build Runs" testid="dossier-builds" items={builds.map((b) => ({
          id: b.run_id, label: b.name, status: b.status, sub: fmtDate(b.started_at),
        }))} link="/builds" projectId={projectId} />
        <Section title="Review Runs" testid="dossier-reviews" items={reviews.map((r) => ({
          id: r.review_id, label: r.review_id, status: r.status, sub: fmtDate(r.created_at),
        }))} link="/reviews" projectId={projectId} />
        <Section title="Audit Runs" testid="dossier-audits" items={audits.map((a) => ({
          id: a.audit_id, label: `${a.name || a.audit_id} · ${a.type === "acceptance" ? "ACCEPTANCE" : "HEALTH"}`, status: a.status, sub: fmtDate(a.created_at),
        }))} link="/audits" projectId={projectId} />
        <Section title="Lieferobjekte" testid="dossier-deliverables" items={deliverables.map((d) => ({
          id: d.deliv_id, label: d.name, status: d.status, sub: `${d.type} · v${d.version}`,
        }))} link="/projects" projectId={projectId} />
        <Section title="Findings" testid="dossier-findings" items={findings.map((f) => ({
          id: f.finding_id, label: f.title, status: f.severity, kind: "severity", sub: fmtDate(f.created_at),
        }))} link="/reviews" projectId={projectId} />
      </div>

      {/* Approvals / Freigaben */}
      <ApprovalsSection
        projectId={projectId}
        approvals={approvals}
        deliverables={deliverables}
        audits={audits}
        onChanged={load}
      />

      {/* Discovery 2.0 — Sessions & Hypothesen (Phase 3a, intern-only) */}
      <Discovery2Section projectId={projectId} />

      {/* Aktenlauf (kuratierter Trail-Auszug) */}
      <ActivityTrailSection trail={trail} />
    </InternalLayout>
  );
}

const TRAIL_TYPE_LABEL = {
  approval_angelegt: "Freigabe angelegt",
  approval_frist_geaendert: "Frist geändert",
  approval_zurueckgezogen: "Freigabe zurückgezogen",
  approval_entschieden: "Freigabe entschieden",
  approval_verfallen: "Freigabe verfallen",
  audit_run_abgeschlossen: "Audit abgeschlossen",
  audit_sign_off_durchgefuehrt: "Sign-off durchgeführt",
  projekt_phase_geaendert: "Phasenwechsel",
  hypothese_entschieden: "Hypothese entschieden",
  discovery_session_abgeschlossen: "Discovery-Session abgeschlossen",
  prompt_set_abgeschlossen: "Prompt-Set abgeschlossen",
  build_run_abgeschlossen: "Build abgeschlossen",
  review_run_abgeschlossen: "Review abgeschlossen",
};

const ActivityTrailSection = ({ trail }) => {
  return (
    <div className="mt-10" data-testid="dossier-trail">
      <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Aktenlauf</div>
      <div className="text-xs text-neutral-500 mb-3">
        Append-only Beweisspur abgeschlossener Akte. Read-only — Korrekturen erfordern neue Akte.
      </div>
      {trail.length === 0 ? (
        <div className="bg-white border border-neutral-200 px-5 py-8 text-center text-sm text-neutral-500" data-testid="trail-empty">
          Noch keine eingetragenen Akte.
        </div>
      ) : (
        <ol className="bg-white border border-neutral-200 divide-y divide-neutral-100">
          {trail.map((ev) => (
            <li key={ev.event_id} className="px-5 py-3 flex items-start gap-4" data-testid={`trail-${ev.event_id}`}>
              <div className="font-mono text-[10px] text-neutral-500 w-36 flex-shrink-0 pt-0.5">
                {fmtDate(ev.occurred_at)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-neutral-900">{ev.summary}</div>
                <div className="font-mono text-[10px] text-neutral-500 mt-0.5">
                  <span className="uppercase tracking-widest">{TRAIL_TYPE_LABEL[ev.event_type] || ev.event_type}</span>
                  {" · "}<span className="text-neutral-700">{ev.actor_name}</span>
                  {" · "}<span className="uppercase tracking-widest">{ev.actor_role}</span>
                  {ev.outcome ? <> · <span className="text-neutral-700">{ev.outcome}</span></> : null}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const ApprovalsSection = ({ projectId, approvals, deliverables, audits, onChanged }) => {
  const [showNew, setShowNew] = React.useState(false);
  const [showDue, setShowDue] = React.useState(null);
  const [showWithdraw, setShowWithdraw] = React.useState(null);
  const [form, setForm] = React.useState({ kind: "entscheidung", target_id: "", title: "", context_text: "", due_at: "" });
  const [dueValue, setDueValue] = React.useState("");
  const [withdrawReason, setWithdrawReason] = React.useState("");

  // ISO Default: 7 Tage in Zukunft, lokal bearbeitbar (datetime-local)
  const isoToLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };
  const localToIso = (loc) => (loc ? new Date(loc).toISOString() : "");
  const defaultLocal = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };

  const eligibleDeliverables = deliverables.filter((d) => d.status === "abgeschlossen");
  const eligibleAuflagen = audits.flatMap((a) =>
    (a.auflagen || []).map((au) => ({ ...au, audit_id: a.audit_id, audit_name: a.name }))
  );

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/approvals`, {
        kind: form.kind,
        target_id: form.kind === "entscheidung" ? null : form.target_id,
        title: form.title,
        context_text: form.context_text,
        due_at: localToIso(form.due_at || defaultLocal()),
      });
      toast.success("Freigabe-Anfrage angelegt");
      setShowNew(false);
      setForm({ kind: "entscheidung", target_id: "", title: "", context_text: "", due_at: "" });
      onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Anlegen fehlgeschlagen");
    }
  };

  const submitDue = async () => {
    try {
      await api.patch(`/projects/${projectId}/approvals/${showDue}/due`, { due_at: localToIso(dueValue) });
      toast.success("Frist aktualisiert");
      setShowDue(null);
      onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Frist-Update fehlgeschlagen");
    }
  };

  const submitWithdraw = async () => {
    if (!withdrawReason.trim()) return;
    try {
      await api.post(`/projects/${projectId}/approvals/${showWithdraw}/withdraw`, { reason: withdrawReason });
      toast.success("Approval zurückgezogen");
      setShowWithdraw(null);
      setWithdrawReason("");
      onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Zurückziehen fehlgeschlagen");
    }
  };

  const KIND_LABEL = {
    lieferobjekt_freigabe: "Lieferobjekt-Freigabe",
    auflage_bestaetigung: "Auflagen-Bestätigung",
    entscheidung: "Entscheidung",
  };

  const STATUS_PILL = {
    offen: { bg: "#DBEAFE", text: "#1E40AF", border: "#93C5FD", label: "Offen" },
    entschieden: { bg: "#DCFCE7", text: "#166534", border: "#86EFAC", label: "Entschieden" },
    verfallen: { bg: "#F4F4F5", text: "#525252", border: "#D4D4D8", label: "Verfallen" },
    zurueckgezogen: { bg: "#FEF2F2", text: "#7F1D1D", border: "#FECACA", label: "Zurückgezogen" },
  };

  return (
    <div className="mt-10" data-testid="dossier-approvals">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Freigaben (Approvals)</div>
          <div className="text-xs text-neutral-500 mt-0.5">Kuratierte Kunden-Entscheidung. Initiierung nur intern, einmalig & unwiderruflich.</div>
        </div>
        <button
          onClick={() => { setForm({ kind: "entscheidung", target_id: "", title: "", context_text: "", due_at: defaultLocal() }); setShowNew(true); }}
          data-testid="approvals-new-button"
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs text-white rounded-sm"
          style={{ background: "#183153" }}
        >
          + Freigabe anfordern
        </button>
      </div>

      <div className="bg-white border border-neutral-200">
        {approvals.length === 0 && (
          <div className="px-5 py-8 text-center text-sm text-neutral-500" data-testid="approvals-empty">
            Noch keine Freigabe-Anfragen.
          </div>
        )}
        {approvals.map((a) => {
          const s = STATUS_PILL[a.status] || STATUS_PILL.offen;
          const isOpen = a.status === "offen";
          return (
            <div key={a.approval_id} className="border-b border-neutral-100 last:border-b-0 px-5 py-3" data-testid={`approval-${a.approval_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-neutral-900">{a.title}</div>
                  <div className="font-mono text-[10px] text-neutral-500 mt-0.5">
                    {a.approval_id} · {KIND_LABEL[a.kind] || a.kind}
                    {a.target_label ? ` · ${a.target_label}` : ""}
                    {" · Frist: "}{fmtDate(a.due_at)}
                  </div>
                  <div className="text-xs text-neutral-700 mt-1.5 whitespace-pre-wrap">{a.context_text}</div>
                  {a.status === "entschieden" && (
                    <div className="mt-2 text-xs">
                      <span className={a.decision === "zustimmung" ? "text-emerald-700 font-medium" : "text-red-700 font-medium"}>
                        {a.decision === "zustimmung" ? "Zugestimmt" : "Abgelehnt"}
                      </span>
                      {" am "}<span className="font-mono">{fmtDate(a.decided_at)}</span>
                      {" durch "}<strong>{a.decided_by_name}</strong>
                      {a.decision_comment && <div className="mt-1 text-neutral-700 italic">„{a.decision_comment}"</div>}
                    </div>
                  )}
                  {a.status === "zurueckgezogen" && (
                    <div className="mt-1 text-xs text-neutral-500">
                      Zurückgezogen am {fmtDate(a.withdrawn_at)} durch {a.withdrawn_by_name} — Grund: {a.withdraw_reason}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center px-2 py-0.5 text-[11px] border rounded-full"
                    style={{ background: s.bg, color: s.text, borderColor: s.border }}
                  >
                    {s.label}
                  </span>
                  {isOpen && (
                    <>
                      <button
                        onClick={() => { setShowDue(a.approval_id); setDueValue(isoToLocal(a.due_at)); }}
                        data-testid={`approval-due-${a.approval_id}`}
                        className="text-xs px-2 py-1 border border-neutral-300 rounded-sm hover:bg-neutral-50"
                      >
                        Frist
                      </button>
                      <button
                        onClick={() => { setShowWithdraw(a.approval_id); setWithdrawReason(""); }}
                        data-testid={`approval-withdraw-${a.approval_id}`}
                        className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded-sm hover:bg-red-50"
                      >
                        Zurückziehen
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Anlegen-Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white border border-neutral-200 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-neutral-200 font-display-medium">Neue Freigabe-Anfrage</div>
            <form onSubmit={onCreate} className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Art <span className="text-red-500">*</span></label>
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value, target_id: "" })}
                  data-testid="approval-kind-select"
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                >
                  <option value="entscheidung">Entscheidung (kein System-Bezug)</option>
                  <option value="lieferobjekt_freigabe">Lieferobjekt-Freigabe</option>
                  <option value="auflage_bestaetigung">Auflagen-Bestätigung</option>
                </select>
              </div>
              {form.kind === "lieferobjekt_freigabe" && (
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Lieferobjekt <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={form.target_id}
                    onChange={(e) => setForm({ ...form, target_id: e.target.value })}
                    data-testid="approval-target-deliverable"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  >
                    <option value="">— wählen —</option>
                    {eligibleDeliverables.map((d) => (
                      <option key={d.deliv_id} value={d.deliv_id}>{d.name} · v{d.version}</option>
                    ))}
                  </select>
                  {eligibleDeliverables.length === 0 && (
                    <div className="text-[11px] text-amber-700 mt-1">Kein abgeschlossenes Lieferobjekt vorhanden.</div>
                  )}
                </div>
              )}
              {form.kind === "auflage_bestaetigung" && (
                <div>
                  <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Auflage <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={form.target_id}
                    onChange={(e) => setForm({ ...form, target_id: e.target.value })}
                    data-testid="approval-target-auflage"
                    className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                  >
                    <option value="">— wählen —</option>
                    {eligibleAuflagen.map((au) => (
                      <option key={au.id} value={au.id}>{au.audit_name} — {au.beschreibung}</option>
                    ))}
                  </select>
                  {eligibleAuflagen.length === 0 && (
                    <div className="text-[11px] text-amber-700 mt-1">Keine Auflagen aus Audits verfügbar.</div>
                  )}
                </div>
              )}
              <input
                required
                placeholder="Titel (kundentauglich, mind. 5 Zeichen)"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="approval-title-input"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <textarea
                required
                rows={4}
                placeholder="Kontext-Text in Kundensprache (mind. 20 Zeichen). Was wird zur Freigabe gestellt? Welche Konsequenz hat die Entscheidung?"
                value={form.context_text}
                onChange={(e) => setForm({ ...form, context_text: e.target.value })}
                data-testid="approval-context-input"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">Frist <span className="text-red-500">*</span></label>
                <input
                  required
                  type="datetime-local"
                  value={form.due_at}
                  onChange={(e) => setForm({ ...form, due_at: e.target.value })}
                  data-testid="approval-due-input"
                  className="w-full mt-1 px-3 py-2 border border-neutral-300 rounded-sm text-sm"
                />
                <div className="text-[11px] text-neutral-500 mt-1">Mindestens 24 h, höchstens 90 Tage in der Zukunft.</div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                <button type="submit" data-testid="approval-submit-button" className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>Anfrage senden</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Frist-Modal */}
      {showDue && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowDue(null)}>
          <div className="bg-white border border-neutral-200 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-neutral-200 font-display-medium">Frist ändern</div>
            <div className="p-5 space-y-3">
              <input
                type="datetime-local"
                value={dueValue}
                onChange={(e) => setDueValue(e.target.value)}
                data-testid="approval-due-edit-input"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <div className="text-[11px] text-neutral-500">Mindestens 24 h, höchstens 90 Tage in der Zukunft.</div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowDue(null)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                <button onClick={submitDue} data-testid="approval-due-confirm" className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>Übernehmen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw-Modal */}
      {showWithdraw && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowWithdraw(null)}>
          <div className="bg-white border border-neutral-200 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-neutral-200 font-display-medium">Anfrage zurückziehen</div>
            <div className="p-5 space-y-3">
              <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2">
                Eine zurückgezogene Anfrage erscheint im Kundenportal als historischer Eintrag. Sie kann nicht reaktiviert werden — bei Bedarf neue Anfrage anlegen.
              </div>
              <textarea
                required
                rows={3}
                placeholder="Begründung (Pflicht)"
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                data-testid="approval-withdraw-reason"
                className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowWithdraw(null)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                <button
                  onClick={submitWithdraw}
                  disabled={!withdrawReason.trim()}
                  data-testid="approval-withdraw-confirm"
                  className="px-4 py-2 text-sm text-white rounded-sm disabled:opacity-50"
                  style={{ background: "#7F1D1D" }}
                >
                  Zurückziehen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, items, link, projectId, testid }) => (
  <div className="bg-white border border-neutral-200" data-testid={testid}>
    <div className="border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
      <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{title}</div>
      <Link to={`${link}?project=${projectId}`} className="text-xs text-[#183153] hover:underline">Öffnen →</Link>
    </div>
    <div className="divide-y divide-neutral-100">
      {items.length === 0 && <div className="px-5 py-6 text-sm text-neutral-500">Noch leer.</div>}
      {items.slice(0, 5).map((it) => (
        <div key={it.id} className="px-5 py-2.5 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm text-neutral-900 truncate">{it.label}</div>
            <div className="font-mono text-[10px] text-neutral-500 truncate">{it.id} · {it.sub}</div>
          </div>
          {!it.kindless ? (
            <StatusPill kind={it.kind || "status"} value={it.status} sharp />
          ) : (
            <span className="font-mono text-xs text-neutral-700">{it.status}</span>
          )}
        </div>
      ))}
    </div>
  </div>
);
